import React, { useState } from 'react';
import { CreateEvent } from '../types/Event';
import { Instrument } from '../types/Instrument';
import { CustomRegistrationQuestion, RegistrationQuestion } from '../types/RegistrationQuestion';
import { formatDate } from '../services/dateService';
import { prettyDateTime } from '../services/formatter.service';

interface CreateEventSummaryPageProps {
  eventDetails: CreateEvent;
  instruments: Instrument[];
  registrationQuestions: RegistrationQuestion[];
  customRegistrationQuestions: CustomRegistrationQuestion[];
  eventType: string;
}

const CreateEventSummaryPage: React.FC<CreateEventSummaryPageProps> = ({
  eventDetails,
  instruments,
  registrationQuestions,
  eventType,
  customRegistrationQuestions
}) => {
  // Get the selected instrument IDs from eventDetails
  const selectedInstrumentIds = eventDetails.instrument_ids || [];

  // Sort instruments by display_order
  const sortedInstruments = instruments
    .filter(instrument => selectedInstrumentIds.includes(instrument.instrument_id)) // Filter to only selected instruments
    .sort((a, b) => a.display_order - b.display_order); // Sort by display_order

  // Step 1: Filter questions based on event type
  const filteredQuestions = registrationQuestions.filter((question) => {
    if (eventType === 'MUSIC') {
      return question.is_music; // Show music-related questions for Music events
    } else if (eventType === 'SPORTS') {
      return question.is_sports; // Show sports-related questions for Sports events
    } else {
      return question.is_general; // Show general questions for general events
    }
  });

  // Step 2: Group questions by category
  const groupedByCategory: Record<string, RegistrationQuestion[]> = filteredQuestions.reduce((groups, question) => {
    const category = question.category || 'Uncategorized'; // Group by category (or 'Uncategorized' if no category is provided)
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(question);
    return groups;
  }, {} as Record<string, RegistrationQuestion[]>);

  // Step 3: Order questions within each category by display_order
  for (const category in groupedByCategory) {
    groupedByCategory[category].sort((a, b) => a.display_order - b.display_order); // Sort by display_order
  }

  return (
    <div className='card' style={{ width: '600px', minWidth: '400px'}}>
      <h2>Event Summary</h2>
      
      <div>
        <h3>Event Details</h3>
        <p style={{marginLeft: '20px'}}><strong>Event Name:</strong> {eventDetails.event_name || 'None'}</p>
        <p style={{marginLeft: '20px'}}><strong>Description:</strong> {eventDetails.event_description || 'None'}</p>
        <p style={{marginLeft: '20px'}}><strong>Event Type:</strong> {eventType}</p>
        <p style={{marginLeft: '20px'}}><strong>Event Size:</strong> {eventDetails.expected_registrant_count} Registrants</p>
        <p style={{marginLeft: '20px'}}><strong>Cyne Up Form Can Have Media:</strong> {eventDetails.has_media ? 'Yes' : 'No'}</p>
        <p style={{marginLeft: '20px'}}><strong>Expected Cost:</strong> ${eventDetails.expected_cost}</p>
        <p style={{marginLeft: '20px'}}><strong>Event Code:</strong> {eventDetails.event_code || 'Not Set'}</p>
        <p style={{marginLeft: '20px'}}><strong>Start Date:</strong> {prettyDateTime(eventDetails.start_date)} </p>
        <p style={{marginLeft: '20px'}}><strong>End Date:</strong> {prettyDateTime(eventDetails.end_date)}</p>
        {/* <p style={{marginLeft: '20px'}}><strong>Registration Limit Per User:</strong> { eventDetails.organization_registrant_limit }</p> */}
        <p style={{marginLeft: '20px'}}><strong>Use Quick Cyne Up:</strong> {eventDetails.checkin_only ? 'Yes' : 'No'}</p>
        {!eventDetails.checkin_only && (<p style={{marginLeft: '20px'}}><strong>Has Checkin:</strong> {eventDetails.has_checkin ? 'Yes' : 'No'}</p>)}
        <p style={{marginLeft: '20px'}}><strong>Has Judging:</strong> {eventDetails.has_judging ? 'Yes' : 'No'}</p>
        {/* <p style={{marginLeft: '20px'}}><strong>Requires Registrant Organization:</strong> {eventDetails.require_organization ? 'Yes' : 'No'}</p> */}
        {eventDetails.use_billing_service && (<p style={{marginLeft: '20px'}}><strong>Registration Fee (Not Including Service Charge):</strong>hi{eventDetails.registrant_fee}</p>)}
        {/* <p style={{marginLeft: '20px'}}><strong>Using Billing Service:</strong> {eventDetails.use_billing_service ? 'Yes' : 'No'}</p> */}
        {/* <p style={{marginLeft: '20px'}}><strong>Requesting Invoice:</strong> {eventDetails.use_invoice ? 'Yes' : 'No'}</p> */}



      </div>

      {eventType === 'MUSIC' && (
        <div>
          <h3>Selected Instruments</h3>
          {sortedInstruments.length > 0 ? (
            sortedInstruments.map((instrument) => (
              <div key={instrument.instrument_id}>
                <p style={{ paddingLeft: '20px' }}><strong>{instrument.instrument_name}</strong></p>
              </div>
            ))
          ) : (
            <p>No instruments selected for this event type.</p>
          )}
        </div>
      )}

      <div>
        <h3>Registration Questions</h3>
        <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>First Name</strong>: Required</p>
        <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>Last Name</strong>: Required</p>
        <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>Email</strong>: Required</p>
        <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>Phone Number</strong>: Required</p>


        {Object.keys(groupedByCategory).map((category) => {
          // Filter the questions in this category to only show "Optional" or "Required" questions
          const validQuestions = groupedByCategory[category].filter(
            (question) => question.option === 'Required' || question.option === 'Optional'
          );

          // Only render the category if it has valid questions
          if (validQuestions.length > 0) {
            return (
              <div key={category} style={{ marginBottom: '30px', paddingLeft: '20px' }}>
                <h4>{category}</h4>
                {validQuestions.map((question) => {
                  const selectedOption = question.option || 'Optional'; // Default to 'Optional' if no option is set

                  return (
                    <div key={question.preset_registration_question_id} style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                      <p>
                        <strong>{question.question_text}</strong>: {selectedOption}
                      </p>
                    </div>
                  );
                })}
              </div>
            );
          }

          return null; // Don't render categories with no valid questions
        })}
      </div>
      {customRegistrationQuestions && customRegistrationQuestions.length > 0 && <div>
        <h4>Custom Registration Questions</h4>
        {customRegistrationQuestions.map((question) => {

                  return (
                    <div key={question.custom_registration_question_id} style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                      <p>
                        <strong>{question.custom_question_text}</strong>: {question.is_required ? 'Required' : 'Optional'}
                      </p>
                    </div>
                  );
                })}
      </div>}
    </div>
  );
};

export default CreateEventSummaryPage;
